import { Accept } from "react-dropzone/.";
import { useCallback, useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { X, CloudUpload, Plus, FileIcon } from "lucide-react";
// ---services
import commonSvc from "../../services/api/common";
import { FileItem } from "../../models/types/common";
import { MediaUrl } from "../../utils/configuration";
import { useTranslation } from "react-i18next";

interface FormDropzoneProps {
    defaultValue?: FileItem[];
    label?: string;
    multiple?: boolean;
    accept?: Accept;
    maxSize?: number; // in bytes
    disabled?: boolean;
    helperText?: string;
    isPreview?: boolean;
    setValue: (files: FileItem[]) => void;
}

const FormDropzone = ({
    defaultValue,
    label,
    multiple = false,
    accept = {
        'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
    },
    maxSize = 10 * 1024 * 1024,
    disabled = false,
    helperText,
    isPreview = false,
    setValue,
}: FormDropzoneProps) => {
    const { t } = useTranslation();
    const [defaultFiles, setDefaultFiles] = useState<FileItem[]>(defaultValue || []);
    const [loading, setLoading] = useState<boolean>(false);

    const handleUpload = async (file: File) => {
        setLoading(true);
        const formData = new FormData();
        formData.append('file', file);
        try {
            const res = await commonSvc.uploadFile(formData);
            console.log('Upload res:', res);
            if (res.content) {
                return { ...res.content, fileName: file.name }
            }
        } catch (error) {
            return null;
        } finally {
            setLoading(false);
        }
    }

    const handleDelete = async (file: FileItem, index: number) => {
        try {
            const response = await commonSvc.deleteFile(Number(file.id));
            console.log('Delete response:', response);
            const newFiles = defaultFiles && defaultFiles.length > 0 ? defaultFiles?.filter((_, i) => i !== index) : [];
            setDefaultFiles(newFiles)
        } catch (error) {
            console.error('Delete error:', error);
        }
    }

    const onDrop = (acceptedFiles: File[]) => {
        if (acceptedFiles.length === 0) return;
        acceptedFiles.forEach(file => {
            handleUpload(file).then((fileItem) => {
                if (fileItem) {
                    defaultFiles && defaultFiles.push(fileItem);
                    setDefaultFiles([...defaultFiles]);
                }
            });
        });
    }

    useEffect(() => {
        console.log('defaultFiles', defaultValue);
        setValue([...defaultFiles]);
    }, [defaultFiles])

    const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({ onDrop, accept, maxSize, disabled, multiple })
    const error = fileRejections.length > 0

    return (
        <div>
            <label className="block text-sm font-medium mb-1 text-gray-700">{label}</label>
            <div className={`transition-all duration-300 relative flex flex-wrap gap-4 border-dashed border border-gray-300 p-4 ${error ? 'border-red-500' : ''}`}>
                {defaultFiles && defaultFiles.length > 0 && (
                    <div className={`flex flex-wrap ${!isPreview ? 'max-w-[1000px]' : ''} gap-4`}>
                        {defaultFiles.map((file, index) => (
                            <div key={index} className="relative">
                                {isPreview ?
                                    <img
                                        src={MediaUrl + file.path}
                                        alt={file.fileName}
                                        className="w-24 h-24 object-cover"
                                        onError={(e) => {
                                            e.currentTarget.src = '/assets/images/no-image.png';
                                        }}
                                    /> :
                                    <div className="bg-gray-100 flex justify-center items-center gap-2 p-2">
                                        <FileIcon size={16} />
                                        <p>{file.fileName}</p>
                                    </div>
                                }
                                <button
                                    className="absolute top-[-10px] right-[-10px]"
                                    type="button"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        handleDelete(file, index)
                                    }}
                                >
                                    <X size={20} className="border rounded-full bg-gray-200 hover:bg-gray-300 hover:text-red-500" />
                                </button>
                            </div>
                        ))}
                    </ div>
                )}
                <div {...getRootProps()} className="min-h-[96px] flex-1 min-w-[200px]">
                    <input {...getInputProps()} />
                    <div className={`h-full w-full border border-radius-md border-dashed border-gray-300 p-2 flex flex-col gap-2`}>
                        <div className="h-full w-full flex items-center justify-center cursor-pointer">
                            {
                                isDragActive ?
                                    <div className="flex flex-col items-center gap-2">
                                        <CloudUpload size={40} className="text-gray-500" />
                                        <p>Drop files here</p>
                                    </div> :
                                    <div className="flex flex-col items-center gap-2">
                                        <Plus size={40} className="text-gray-500" />
                                        <p>{t('common.dropzone')}</p>
                                    </div>
                            }
                        </div>
                    </div>
                    {error && (
                        <div className="absolute bottom-0 left-0 right-0 text-red-500 mt-2">
                            <p>File upload errors:</p>
                            <ul>
                                {fileRejections.map(({ file, errors }) => (
                                    <li key={file.name}>
                                        {file.name} - {errors.map(e => e.message).join(', ')}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default FormDropzone;
